/* Foundry Insights — plain-English glossary for the info bubbles (InsInfoTip).
 * Flat { termKey: helpText } map spread over the cross-report COMMON_TIPS, matching the design-system
 * convention (see Insights/insightsGlossary.js + README). Consumed as:
 *   <InsInfoTip term-key="scrap" :glossary="fieldTips" />           // KPI / chart title
 *   <InsInfoTip term-key="bad_qty" :glossary="fieldTips" />         // table column header
 * One entry per DISTINCT field name; shared columns (part, bad_qty, est_cost…) read consistently across
 * the scrap tables. Metric-level bubbles mirror the backend ProvenanceMetrics() `basis` text, condensed.
 */
import { COMMON_TIPS } from "../Insights/insightsGlossary";

const fieldTips = {
  ...COMMON_TIPS,

  // ---- KPI cards / chart titles (mirror FoundryInsightsController ProvenanceMetrics basis) ----
  // NOTE: the top KPI cards resolve by their overview `c.key` — which are `shipped` and `scrap_cost`
  // (not `shipped_weight`/`scrap`). Aliases below keep those cards' bubbles working.
  shipped: "Total foundry iron shipped (tons) — VMFG shipment lines valued at each part's unit weight. Reconciled against the Foundry Monthly Reports sheet.",
  scrap_cost: "Foundry scrap cost ($) by month from the Foundry Monthly Reports workbook. Higher is worse; the VMFG scrap drill is a reason-coded subset of this $.",
  shipped_weight: "Total foundry iron shipped each month — VMFG shipment lines valued at each part's unit weight. Reconciled against the Foundry Monthly Reports sheet.",
  alloy_split: "Share of foundry output by alloy (28PC / Ductile / ADI / Gray) each month, from the Foundry Monthly Reports workbook.",
  parts_sales: "Invoiced parts revenue by month — VMFG receivable lines where the order line has a part id, net of credit memos.",
  hrs_per_ton: "Labor hours per ton by foundry department (Paychex hours ÷ tons shipped). Lower is more efficient; each department has its own goal line.",
  overtime: "Overtime as a share of total hours by month, from Paychex payroll (OT hours ÷ total hours).",
  scrap: "Headline foundry scrap cost ($) and scrap rate (%) by month from the Foundry Monthly Reports workbook. The VMFG scrap drill is a reason-coded subset of this $.",
  scrap_detail: "Drill behind Scrap: reason-coded scrap $ (a ~40% reason-coded subset of the headline scrap $) and scrap unit counts by inferred shift × workstation.",
  melt_ship: "Iron melted-to-shipped ratio (tons melted ÷ tons shipped), weighted YTD. LOWER is better — a proxy for foundry yield / rework.",
  chrome_cost: "Monthly chrome alloy spend ($) used in the foundry — total chrome cost by month.",
  chrome_blended: "Blended chrome cost per pound = total chrome $ ÷ total chrome lbs. The effective unit price paid across carbon grades.",

  // ---- scrap tables (Top Contributors / Breakdown / by Shift) ----
  rank: "Rank order within this table (highest first).",
  item: "The part / item that was scrapped.",
  events: "Number of scrap events (labor tickets) recorded for this item in the window.",
  bad_qty: "Total quantity scrapped.",
  est_cost: "Estimated scrap cost (reason-coded subset — ~40% of the headline scrap $).",
  month: "The month the scrap was recorded.",
  reason: "Deviation / reason code the scrap was booked against (reason-coded subset).",
  workstation: "Workstation inferred from the labor-ticket clock-in.",
  part: "The part that was scrapped.",

  // ---- Shipped to Customer by Alloy ----
  customer: "Customer the foundry iron was shipped to (~6-month window).",

  // ---- Foundry Inventory — Latest Count ----
  material: "Foundry raw / in-process material.",
  section: "Storage section / location the count was taken in.",
  on_hand: "Latest counted quantity on hand.",
  uom: "Unit of measure the on-hand quantity is expressed in.",

  // ---- drill modals (month invoices, chrome detail, customer shipment lines) ----
  invoice: "VMFG parts invoice number.",
  date: "Invoice date.",
  inv_type: "Invoice type — I = invoice, M = credit memo.",
  amount: "Invoiced amount for the row.",
  carbon_grade: "Carbon grade of the chrome alloy used.",
  used_lbs: "Pounds of chrome used at this grade.",
  usd_lb: "Unit price paid per pound at this grade.",
  cost: "Chrome cost = pounds × unit price.",
  order_no: "Customer order the shipment line belongs to.",
  alloy: "Alloy of the shipped part (from the part-id suffix).",
  pieces: "Pieces shipped on the line.",
  lbs: "Pounds shipped on the line (pieces × part unit weight).",

  // ---- Kepware (in-house ALAB clay-extrusion test machine; many fields are Tier-2/3 confidence) ----
  kep_year: "Calendar year of the batches in this bucket.",
  kep_mode: "ALAB control mode code (0–9). Exact meaning per code is UNCONFIRMED (Tier 2) — not simply on/off.",
  kep_moisture_band: "Moisture bucket in 0.1% bands (e.g. 3.3 = 3.30–3.39% moisture).",
  kep_clean: "1 = clean batch, 0 = outlier. The outlier toggle uses this to include/exclude noisy batches.",
  kep_batches: "Number of batches aggregated into this bucket.",
  kep_avg_moisture: "Mean moisture % for the bucket (Σ moisture ÷ batches).",
  kep_sigma: "Standard deviation of moisture — tightness of control. Lower is better.",
  kep_green_strength: "Mean green (unfired) strength of the sand mix for the bucket.",
  kep_amperage: "Mean mixer/extruder amperage — a proxy for mix stiffness/load.",
  kep_dosing_error: "Mean absolute water-dosing error. Lower = tighter dosing.",
};

export default fieldTips;
